import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Search, Plus, Check, ShoppingCart, Home, Calendar, Clock, MapPin, Phone, Shield, X, Send, Loader2, Sparkles, MessageCircle, FlaskRound, TestTube, Droplets, Timer } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Checkbox } from '../components/ui/checkbox';
import { toast } from 'sonner';
import { useCart } from '../contexts/CartContext';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/* ═══ AI Smart Test Finder ═══ */
const SmartTestFinder = () => {
  const [symptoms, setSymptoms] = useState('');
  const [result, setResult] = useState('');
  const [loading, setLoading] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const resultRef = useRef(null);

  const handleRecommend = async () => {
    if (!symptoms.trim()) { toast.error('Please describe your symptoms'); return; }
    setLoading(true);
    setResult('');
    try {
      const { data } = await axios.post(`${API}/ai/recommend-tests`, { symptoms });
      setResult(data.recommendation || 'No recommendations available.');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to get recommendations');
    }
    setLoading(false);
    setTimeout(() => resultRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' }), 200);
  };

  const formatMarkdown = (text) => {
    if (!text) return '';
    return text
      .replace(/### \*\*(.*?)\*\*/g, '<h3 class="font-bold text-[#0A2540] text-base mt-4 mb-2">$1</h3>')
      .replace(/\*\*(.*?)\*\*/g, '<strong class="font-semibold text-[#0A2540]">$1</strong>')
      .replace(/\*(.*?)\*/g, '<em class="text-[#475569]">$1</em>')
      .replace(/^- /gm, '&bull; ')
      .replace(/\n\n/g, '<br/><br/>')
      .replace(/\n/g, '<br/>');
  };

  return (
    <section className="px-4 py-4">
      <div className="max-w-6xl mx-auto">
        <button onClick={() => setIsOpen(!isOpen)}
          className="w-full bg-gradient-to-r from-violet-600 to-indigo-600 text-white rounded-2xl p-4 sm:p-5 flex items-center justify-between hover:from-violet-700 hover:to-indigo-700 transition-all shadow-lg"
          data-testid="smart-test-finder-toggle">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-white/20 rounded-xl"><Sparkles className="h-5 w-5" /></div>
            <div className="text-left">
              <p className="font-bold text-sm sm:text-base">AI Smart Test Finder</p>
              <p className="text-xs text-white/70">Describe your symptoms, get personalized test recommendations</p>
            </div>
          </div>
          <span className="text-white/60 text-xl">{isOpen ? '−' : '+'}</span>
        </button>
        {isOpen && (
          <div className="bg-white rounded-b-2xl -mt-2 pt-6 p-4 sm:p-5 shadow-lg border border-t-0 border-[#E2E8F0]">
            <div className="flex gap-2">
              <Input value={symptoms} onChange={e => setSymptoms(e.target.value)} placeholder="e.g. I have frequent headaches, dizziness, and fatigue..."
                className="h-12 rounded-xl flex-1 text-sm" onKeyDown={e => e.key === 'Enter' && handleRecommend()} data-testid="ai-symptoms-input" />
              <Button onClick={handleRecommend} disabled={loading} className="bg-indigo-600 hover:bg-indigo-700 rounded-xl h-12 px-5" data-testid="ai-recommend-btn">
                {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
              </Button>
            </div>
            {loading && (
              <div className="flex items-center gap-2 mt-4 text-sm text-indigo-600">
                <Loader2 className="h-4 w-4 animate-spin" /><span>Analyzing symptoms and finding the best tests for you...</span>
              </div>
            )}
            {result && (
              <div ref={resultRef} className="mt-4 bg-[#F8FAFC] rounded-xl p-4 sm:p-5 border border-[#E2E8F0]" data-testid="ai-result">
                <div className="flex items-center gap-2 mb-3">
                  <Sparkles className="h-4 w-4 text-indigo-600" />
                  <span className="text-xs font-bold text-indigo-600 uppercase tracking-wide">AI Recommendations</span>
                </div>
                <div className="prose prose-sm max-w-none text-sm text-[#334155] leading-relaxed" dangerouslySetInnerHTML={{ __html: formatMarkdown(result) }} />
              </div>
            )}
          </div>
        )}
      </div>
    </section>
  );
};

/* ═══ Main Lab Tests Page ═══ */
const LabTests = () => {
  const { cartItems, addToCart, removeFromCart, getTotal, clearCart } = useCart();
  const [catalog, setCatalog] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('');
  const [showCheckout, setShowCheckout] = useState(false);
  const [formData, setFormData] = useState({
    customer_name: '', phone: '', email: '', address: '',
    home_collection: false, collection_date: '', collection_time: '',
  });

  const fetchCatalog = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (selectedCategory) params.set('category', selectedCategory);
      if (searchQuery) params.set('search', searchQuery);
      const { data } = await axios.get(`${API}/lab-tests?${params}`);
      setCatalog(data);
    } catch { toast.error('Failed to load catalog'); }
    setLoading(false);
  }, [selectedCategory, searchQuery]);

  useEffect(() => {
    axios.get(`${API}/lab-tests/categories`).then(r => setCategories(r.data)).catch(() => {});
  }, []);

  useEffect(() => { fetchCatalog(); }, [fetchCatalog]);

  const isInCart = (name) => cartItems.some(item => item.test_name === name || item.name === name);

  const handleAddToCart = (test) => {
    const price = test.offer_price || test.mrp;
    addToCart({ ...test, test_name: test.name, price, category: test.category });
    toast.success(`${test.name} added to cart`);
  };

  const handleRemoveFromCart = (name) => { removeFromCart(name); };

  const sendWhatsAppNotification = (details) => {
    const phone = '917010200869';
    const testList = details.tests.map(t => `${t.test_name || t.name} (Rs.${t.price})`).join(', ');
    const message = `NEW BOOKING - Aaric Scans & Diagnostics\nName: ${details.customer_name}\nPhone: ${details.phone}\nEmail: ${details.email}\nTests: ${testList}\nTotal: Rs.${details.total_amount}\nHome Collection: ${details.home_collection ? 'Yes' : 'No'}${details.home_collection ? `\nDate: ${details.collection_date} Time: ${details.collection_time}` : ''}\nAddress: ${details.address}`;
    window.open(`https://wa.me/${phone}?text=${encodeURIComponent(message)}`, '_blank');
  };

  const handleSubmitOrder = async (e) => {
    e.preventDefault();
    if (cartItems.length === 0) { toast.error('Cart is empty'); return; }
    if (!formData.customer_name || !formData.phone) { toast.error('Name and phone required'); return; }
    try {
      const orderData = { ...formData, tests: cartItems, total_amount: getTotal() };
      await axios.post(`${API}/lab-orders`, orderData);
      toast.success('Booking confirmed! We will contact you shortly.');
      sendWhatsAppNotification(orderData);
      clearCart();
      setFormData({ customer_name: '', phone: '', email: '', address: '', home_collection: false, collection_date: '', collection_time: '' });
      setShowCheckout(false);
    } catch { toast.error('Failed to place order'); }
  };

  // Group by category
  const grouped = {};
  catalog.forEach(t => { if (!grouped[t.category]) grouped[t.category] = []; grouped[t.category].push(t); });

  return (
    <div className="min-h-screen bg-[#F8FAFC]">
      {/* Hero */}
      <section className="bg-gradient-to-r from-[#0A2540] to-[#0EA5E9] py-12 sm:py-16 px-4">
        <div className="max-w-5xl mx-auto text-center">
          <div className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-sm rounded-full px-5 py-2 mb-4">
            <Shield className="h-4 w-4 text-green-300" />
            <span className="text-white text-sm font-semibold">NABL M(EL)T Accredited</span>
          </div>
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-3 tracking-tight" style={{ fontFamily: 'Outfit, sans-serif' }} data-testid="lab-tests-heading">
            Aaric Scans & Diagnostics
          </h1>
          <p className="text-base sm:text-lg text-white/85 mb-4 max-w-2xl mx-auto">
            97+ lab tests with transparent pricing. Home collection available across Chennai.
          </p>
          <a href="/health-packages" className="inline-flex items-center gap-2 bg-white/15 hover:bg-white/25 text-white rounded-full px-6 py-2.5 text-sm font-semibold border border-white/20 transition-all" data-testid="view-packages-link">
            View Health Packages & Profiles
          </a>
        </div>
      </section>

      {/* AI Smart Test Finder */}
      <SmartTestFinder />

      {/* Search Bar + Cart */}
      <section className="py-3 px-4 bg-white border-b sticky top-0 z-40 shadow-sm">
        <div className="max-w-6xl mx-auto flex items-center gap-3 flex-wrap">
          <div className="relative flex-1 min-w-[200px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#94A3B8]" />
            <Input placeholder="Search tests by name or category..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} className="pl-10 h-10 rounded-xl" data-testid="search-tests-input" />
          </div>
          {cartItems.length > 0 && (
            <Button onClick={() => setShowCheckout(!showCheckout)} className="bg-[#0EA5E9] text-white rounded-xl h-10 px-4" data-testid="proceed-to-checkout-button">
              <ShoppingCart className="h-4 w-4 mr-1" /> {cartItems.length} | Rs.{getTotal()}
            </Button>
          )}
        </div>
      </section>

      {/* Category Tabs */}
      <section className="px-4 py-3 bg-white border-b">
        <div className="max-w-6xl mx-auto flex gap-2 overflow-x-auto pb-1">
          <button onClick={() => setSelectedCategory('')}
            className={`px-4 py-2 rounded-xl text-xs font-bold whitespace-nowrap ${!selectedCategory ? 'bg-[#0A2540] text-white' : 'bg-[#F1F5F9] text-[#475569]'}`}
            data-testid="category-tab-all">All</button>
          {categories.map(cat => (
            <button key={cat} onClick={() => setSelectedCategory(cat)}
              className={`px-4 py-2 rounded-xl text-xs font-bold whitespace-nowrap ${selectedCategory === cat ? 'bg-[#0A2540] text-white' : 'bg-[#F1F5F9] text-[#475569]'}`}
              data-testid={`category-tab-${cat.toLowerCase().replace(/[\s/]+/g, '-')}`}>{cat}</button>
          ))}
        </div>
      </section>

      <div className="max-w-6xl mx-auto px-4 py-6">
        {showCheckout ? (
          <div className="bg-white rounded-2xl p-6 shadow-lg max-w-2xl mx-auto">
            <h2 className="text-2xl font-bold text-[#0A2540] mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>Book Tests</h2>
            <div className="mb-6 space-y-2">
              {cartItems.map(item => (
                <div key={item.test_name || item.name} className="flex items-center justify-between p-3 bg-[#F8FAFC] rounded-xl">
                  <div><p className="font-medium text-sm text-[#0A2540]">{item.test_name || item.name}</p><p className="text-xs text-[#475569]">{item.category}</p></div>
                  <div className="flex items-center gap-3">
                    <span className="font-bold text-[#0A2540]">Rs.{item.price}</span>
                    <button onClick={() => handleRemoveFromCart(item.test_name || item.name)} className="text-red-400 hover:text-red-600"><X className="h-4 w-4" /></button>
                  </div>
                </div>
              ))}
              <div className="pt-3 border-t flex justify-between"><span className="font-bold text-lg">Total</span><span className="font-bold text-lg text-[#0EA5E9]">Rs.{getTotal()}</span></div>
            </div>
            <form onSubmit={handleSubmitOrder} className="space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Full Name *</Label><Input name="customer_name" value={formData.customer_name} onChange={e => setFormData(p => ({ ...p, customer_name: e.target.value }))} required className="h-11 rounded-xl mt-1" data-testid="checkout-name-input" /></div>
                <div><Label>Phone *</Label><Input name="phone" value={formData.phone} onChange={e => setFormData(p => ({ ...p, phone: e.target.value }))} required className="h-11 rounded-xl mt-1" data-testid="checkout-phone-input" /></div>
              </div>
              <div><Label>Email</Label><Input name="email" value={formData.email} onChange={e => setFormData(p => ({ ...p, email: e.target.value }))} className="h-11 rounded-xl mt-1" data-testid="checkout-email-input" /></div>
              <div><Label>Address</Label><Input name="address" value={formData.address} onChange={e => setFormData(p => ({ ...p, address: e.target.value }))} className="h-11 rounded-xl mt-1" data-testid="checkout-address-input" /></div>
              <div className="flex items-center space-x-2">
                <Checkbox id="home_collection" checked={formData.home_collection} onCheckedChange={v => setFormData(p => ({ ...p, home_collection: v }))} data-testid="home-collection-checkbox" />
                <label htmlFor="home_collection" className="text-sm font-medium flex items-center"><Home className="h-4 w-4 mr-2 text-[#0EA5E9]" /> Home Sample Collection</label>
              </div>
              {formData.home_collection && (
                <div className="grid grid-cols-2 gap-3 bg-[#0EA5E9]/5 p-4 rounded-xl">
                  <div><Label><Calendar className="h-3 w-3 inline mr-1" />Date *</Label><Input type="date" value={formData.collection_date} onChange={e => setFormData(p => ({ ...p, collection_date: e.target.value }))} required={formData.home_collection} className="h-11 rounded-xl mt-1" data-testid="collection-date-input" /></div>
                  <div><Label><Clock className="h-3 w-3 inline mr-1" />Time *</Label><Input type="time" value={formData.collection_time} onChange={e => setFormData(p => ({ ...p, collection_time: e.target.value }))} required={formData.home_collection} className="h-11 rounded-xl mt-1" data-testid="collection-time-input" /></div>
                </div>
              )}
              <Button type="submit" className="w-full bg-[#0A2540] text-white rounded-xl py-3 text-lg font-bold" data-testid="submit-lab-order-button">
                <MessageCircle className="h-5 w-5 mr-2" /> Confirm Booking - Rs.{getTotal()}
              </Button>
            </form>
          </div>
        ) : (
          <>
            {loading ? <p className="text-center text-[#94A3B8] py-8">Loading catalog...</p> : Object.keys(grouped).length === 0 ? <p className="text-center text-[#94A3B8] py-8">No tests found</p> : (
              <div className="space-y-6">
                {Object.entries(grouped).map(([cat, items]) => (
                  <div key={cat}>
                    <h2 className="text-lg font-bold text-[#0A2540] mb-3" style={{ fontFamily: 'Outfit, sans-serif' }} data-testid={`category-${cat.toLowerCase().replace(/[\s/]+/g, '-')}`}>
                      {cat} <span className="text-sm font-normal text-[#94A3B8]">({items.length})</span>
                    </h2>
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                      {items.map(t => {
                        const inCart = isInCart(t.name);
                        const hasOffer = t.offer_price && t.offer_price < t.mrp;
                        return (
                          <div key={t.test_code} className="bg-white border border-[#E2E8F0] rounded-xl p-4 hover:shadow-md transition-all" data-testid={`test-card-${t.test_code}`}>
                            <h3 className="font-semibold text-[#0A2540] text-sm mb-2 leading-tight">{t.name}</h3>
                            <div className="flex flex-wrap gap-x-3 gap-y-1 text-[11px] text-[#64748B] mb-2">
                              {t.sample_requirement && <span className="flex items-center gap-0.5"><Droplets className="h-3 w-3" /> {t.sample_requirement}</span>}
                              {t.tat && <span className="flex items-center gap-0.5"><Timer className="h-3 w-3" /> {t.tat}</span>}
                              {t.home_collection && <span className="flex items-center gap-0.5 text-green-600"><Home className="h-3 w-3" /> Home</span>}
                            </div>
                            {t.preparation && <p className="text-[10px] text-amber-600 bg-amber-50 px-2 py-1 rounded-lg mb-2">{t.preparation}</p>}
                            <div className="flex items-baseline gap-2 mb-3">
                              {hasOffer ? (
                                <>
                                  <span className="text-lg font-bold text-[#0EA5E9]">Rs.{t.offer_price}</span>
                                  <span className="text-sm text-[#94A3B8] line-through">Rs.{t.mrp}</span>
                                </>
                              ) : (
                                <span className="text-lg font-bold text-[#0A2540]">Rs.{t.mrp}</span>
                              )}
                            </div>
                            <Button onClick={() => inCart ? handleRemoveFromCart(t.name) : handleAddToCart(t)}
                              variant={inCart ? 'outline' : 'default'}
                              className={`w-full rounded-xl text-sm h-9 ${inCart ? 'border-2 border-green-500 text-green-600' : 'bg-[#0A2540] text-white'}`}
                              data-testid={`add-to-cart-${t.test_code}`}>
                              {inCart ? <><Check className="h-3 w-3 mr-1" /> Added</> : <><Plus className="h-3 w-3 mr-1" /> Add to Cart</>}
                            </Button>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </>
        )}
      </div>

      {/* Home Service CTA */}
      {!showCheckout && (
        <section className="py-10 px-4 bg-gradient-to-r from-[#0A2540] to-[#0EA5E9]">
          <div className="max-w-4xl mx-auto text-center text-white">
            <h2 className="text-2xl font-bold mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>Home Sample Collection</h2>
            <p className="text-white/80 mb-4">Book any test with home collection. Our phlebotomist visits your doorstep.</p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
              <a href="https://wa.me/916380608090?text=Hi%2C%20I%20want%20to%20book%20a%20home%20sample%20collection" target="_blank" rel="noopener noreferrer">
                <Button className="bg-[#25D366] hover:bg-[#1da851] text-white rounded-full px-8 py-3 font-semibold">
                  <MessageCircle className="mr-2 h-5 w-5" /> Book via WhatsApp
                </Button>
              </a>
              <a href="tel:6380608090">
                <Button variant="outline" className="border-2 border-white text-white hover:bg-white/10 rounded-full px-8 py-3 font-semibold">
                  <Phone className="mr-2 h-4 w-4" /> Call: 6380608090
                </Button>
              </a>
            </div>
          </div>
        </section>
      )}
    </div>
  );
};

export default LabTests;
